import {
  faMedal,
  faTrophy,
  faStar,
  faGem,
  faCrown,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons";

// Shared VIP tier presentation metadata (dashboard card + VIP Tiers page).
// Tier index is the position in the ladder (0-based), NOT tier_order.
const TIER_STYLES = [
  { name: "VIP 1", color: "#cd8a4d", icon: faMedal },
  { name: "VIP 2", color: "#b8c4c2", icon: faMedal },
  { name: "VIP 3", color: "#e6c06a", icon: faTrophy },
  { name: "VIP 4", color: "#9ad8d0", icon: faStar },
  { name: "VIP 5", color: "#7cc4ff", icon: faGem },
  { name: "VIP 6", color: "#c9a2ff", icon: faCrown },
];

export const GRACE_ICON = faShieldHalved;

export const tierStyle = (idx) =>
  TIER_STYLES[Math.min(Math.max(idx, 0), TIER_STYLES.length - 1)] || TIER_STYLES[0];

export const fmtUsd = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000_000) return "$" + (v / 1_000_000_000).toFixed(v >= 10_000_000_000 ? 0 : 1) + "B";
  if (v >= 1_000_000) return "$" + (v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1) + "M";
  if (v >= 1_000) return "$" + (v / 1_000).toFixed(v >= 10_000 ? 0 : 1) + "k";
  return "$" + v.toLocaleString("en-US", { maximumFractionDigits: 0 });
};

/**
 * Normalize the ppapi `discountTier` payload into everything the VIP UIs need.
 * Returns null when there is nothing meaningful to render (guards the pre-ppapi
 * rollout state and empty ladders).
 */
export function resolveVipState(pd) {
  if (!pd || typeof pd !== "object") return null;
  if (!Array.isArray(pd.tiers) && pd.discountPct == null) return null;

  const tiers = (Array.isArray(pd.tiers) ? [...pd.tiers] : [])
    .map((t) => ({
      order: Number(t.order) || 0,
      minVolumeUsd: Number(t.minVolumeUsd) || 0,
      discountPct: Number(t.discountPct) || 0,
    }))
    .sort((a, b) => a.minVolumeUsd - b.minVolumeUsd);

  const discountPct = Number(pd.discountPct) || 0;
  if (tiers.length === 0 && discountPct <= 0) return null;

  const vol30 = Number(pd.volume30dUsd) || 0;

  let currentIdx = -1;
  for (let i = 0; i < tiers.length; i++) {
    if (vol30 >= tiers[i].minVolumeUsd) currentIdx = i;
  }
  const current = currentIdx >= 0 ? tiers[currentIdx] : null;
  const next = currentIdx + 1 < tiers.length ? tiers[currentIdx + 1] : null;

  // Tier actually applied (the discount the partner receives). During a grace
  // period this sits ABOVE the volume tier, so the badge / "your tier" must
  // follow the discount; the milestone track stays volume-based (currentIdx).
  let badgeIdx = tiers.findIndex((t) => t.discountPct === discountPct);
  if (badgeIdx < 0) badgeIdx = currentIdx;
  const inGrace = Boolean(pd.graceUntil) && badgeIdx > currentIdx;

  // Milestone fill across an evenly-spaced track: Start node + one node per tier.
  const nodeCount = tiers.length + 1;
  let partial = 0;
  if (next) {
    const base = current ? current.minVolumeUsd : 0;
    const span = next.minVolumeUsd - base;
    partial = span > 0 ? Math.max(0, Math.min(1, (vol30 - base) / span)) : 0;
  } else if (tiers.length > 0) {
    partial = 1;
  }
  const achievedNodes = currentIdx + 1; // start node is always achieved
  const trackPct = nodeCount > 1
    ? Math.min(100, ((achievedNodes + (next ? partial : 0)) / (nodeCount - 1)) * 100)
    : 0;

  return {
    tiers,
    discountPct,
    vol30,
    current,
    currentIdx,
    badgeIdx,
    inGrace,
    next,
    remaining: next ? Math.max(0, next.minVolumeUsd - vol30) : 0,
    trackPct,
    graceUntil: pd.graceUntil || null,
    volumes: {
      daily: Number(pd?.volumes?.daily) || 0,
      monthly: Number(pd?.volumes?.monthly) || 0,
      allTime: Number(pd?.volumes?.allTime) || 0,
    },
  };
}
